import type { ApiContent, ApiMessage } from '@/lib/stream';
import type { Version } from '@/lib/schemas';

/**
 * Pure helpers for assembling Anthropic Messages API requests from chat history +
 * the currently-selected version. Kept out of the Zustand store so they stay
 * unit-testable and so the store file stays focused on state shape + actions.
 */

export const MAX_ARTIFACT_CONTEXT_CHARS = 60_000;

/** Cheap structural summary for too-large artifacts. Keeps tag tree, drops inner text. */
/** Pick a fenced-block language tag from a media type or filename. */
function inferLangFromMediaType(mediaType: string, name: string): string {
  if (mediaType === 'image/svg+xml') return 'svg';
  if (mediaType === 'text/html') return 'html';
  if (mediaType === 'text/css') return 'css';
  if (mediaType === 'application/json') return 'json';
  if (mediaType === 'text/markdown' || /\.md$/i.test(name)) return 'md';
  if (mediaType === 'text/javascript' || /\.(?:js|mjs)$/i.test(name)) return 'js';
  if (mediaType === 'text/x-typescript' || /\.tsx?$/i.test(name)) return 'ts';
  return '';
}

// Inline <script> blocks longer than this get the script-aware outline treatment.
const LARGE_SCRIPT_THRESHOLD = 500;

/**
 * Build a compact structural outline of a JavaScript block so the model knows
 * what can be edited without paying the token cost of the full source.
 *
 * Readable scripts → list of top-level function/class/const declarations,
 * including the literal value for string constants (key for find-replace hints).
 * Minified scripts → size badge + find-replace instruction.
 *
 * "Minified" heuristic: average line length > 200 chars (minifiers collapse
 * everything onto one or a handful of very long lines).
 */
export function buildScriptOutline(content: string): string {
  const sizeKb = Math.round(content.length / 1024) || 1;
  const lines = content.split('\n');
  const avgLineLen = content.length / Math.max(lines.length, 1);

  if (avgLineLen > 200) {
    return (
      `/* ~${sizeKb} KB minified bundle — full source hidden to stay within token budget.\n` +
      `   Use <edit action="find-replace" find="..." replace="..."> to change specific text. */`
    );
  }

  // Readable script — extract top-level declarations line by line.
  const decls: string[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trimStart();
    if (!line || line.startsWith('//') || line.startsWith('*') || line.startsWith('/*')) continue;

    // function / async function
    const fn = /^(?:export\s+)?(?:async\s+)?function\s+([$A-Za-z_][\w$]*)\s*\(([^)]{0,60})\)/.exec(line);
    if (fn) { decls.push(`function ${fn[1]}(${fn[2]})`); continue; }

    // class
    const cls = /^(?:export\s+)?class\s+([$A-Za-z_][\w$]*)(?:\s+extends\s+([$A-Za-z_][\w$.]+))?/.exec(line);
    if (cls) { decls.push(cls[2] ? `class ${cls[1]} extends ${cls[2]}` : `class ${cls[1]}`); continue; }

    // const/let/var — include string literal value when present (crucial for find-replace)
    const decl = /^(?:export\s+)?(?:const|let|var)\s+([$A-Za-z_][\w$]*)\s*=\s*(['"`])([^'"`\n]{0,60})\2/.exec(line);
    if (decl) { decls.push(`${decl[1]} = '${decl[3]}'`); continue; }

    // const/let/var without a string value — just the name
    const bare = /^(?:export\s+)?(?:const|let|var)\s+([$A-Za-z_][\w$]*)/.exec(line);
    if (bare) { decls.push(bare[1]); }
  }

  const MAX_SHOWN = 25;
  const shown = decls.slice(0, MAX_SHOWN);
  const extra = decls.length > MAX_SHOWN ? `\n   … and ${decls.length - MAX_SHOWN} more` : '';

  if (shown.length === 0) {
    return `/* ~${sizeKb} KB inline script (structure not extractable) */`;
  }

  return (
    `/* ~${sizeKb} KB inline script. Top-level declarations:\n` +
    `   ${shown.join('\n   ')}${extra}\n` +
    `   Use <edit action="find-replace" find="..." replace="..."> to change specific values. */`
  );
}

export function structuralOutline(html: string, opts: { includeScript?: boolean } = {}): string {
  let stripped = html;

  if (!opts.includeScript) {
    // Replace large inline <script> blocks with a compact structural outline.
    // External <script src="..."> references and small scripts are left intact.
    stripped = stripped.replace(
      /<script\b([^>]*)>([\s\S]*?)<\/script>/gi,
      (match, attrs: string, content: string) => {
        if (/\bsrc\s*=/.test(attrs)) return match;
        if (content.length <= LARGE_SCRIPT_THRESHOLD) return match;
        return `<script${attrs}>\n${buildScriptOutline(content)}\n</script>`;
      },
    );
  }

  // Only strip <style> blocks when the document is still over budget after
  // script compaction. Keeping CSS in context lets the model write accurate
  // selectors and find-replace patterns for style edits.
  if (stripped.length > MAX_ARTIFACT_CONTEXT_CHARS) {
    stripped = stripped.replace(/<style\b[\s\S]*?<\/style>/gi, '<style>/* …truncated… */</style>');
  }

  if (stripped.length <= MAX_ARTIFACT_CONTEXT_CHARS) return stripped;
  return stripped.slice(0, MAX_ARTIFACT_CONTEXT_CHARS) + '\n<!-- truncated for context budget -->';
}

export function stripAssistantPlumbing(content: string): string {
  return content.replace(/<\/?artifact>/g, '').trim();
}

export function tryParseAnthropicError(bodyText: string): string | null {
  if (!bodyText) return null;
  try {
    const parsed = JSON.parse(bodyText) as { error?: { message?: unknown } };
    if (parsed.error && typeof parsed.error.message === 'string') return parsed.error.message;
  } catch {
    /* ignore */
  }
  return null;
}

// Re-import the types we need at signature time. Mirrors the previous in-store shape.
export interface ChatRowForApi {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  status: 'pending' | 'streaming' | 'complete' | 'error' | 'cancelled' | 'partial';
}

export type AttachmentForApi =
  | {
      kind: 'image';
      data: string;
      mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
    }
  | { kind: 'document'; data: string; name: string }
  | { kind: 'text-file'; name: string; mediaType: string; content: string };

/**
 * Reconstruct the conversation for the API. Critical detail: the selected
 * version's HTML always lands in the new user turn under `<current_design>`,
 * never in a prior assistant turn. The system-prompt selector in `sendMessage`
 * keys on `<current_design>` to apply EDIT_MODE_DIRECTIVE — embedding the
 * artifact in an assistant turn would silently downgrade iteration to full-doc
 * regen, which has been the source of multiple regressions.
 */
export function buildApiMessages(
  priorMessages: ChatRowForApi[],
  versions: Version[],
  selectedVersionId: string | null,
  newUserText: string,
  attachments: AttachmentForApi[],
  opts: { includeScript?: boolean } = {},
): ApiMessage[] {
  const eligible = priorMessages.filter(
    (m) =>
      m.role === 'user' ||
      m.role === 'system' ||
      (m.role === 'assistant' && m.status === 'complete'),
  );
  const trimmed = eligible.slice(-12);

  const selectedVersion =
    (selectedVersionId ? versions.find((v) => v.id === selectedVersionId) : null) ??
    [...versions].reverse().find((v) => v.status === 'complete' && v.htmlFinal) ??
    null;

  const raw: ApiMessage[] = trimmed.map((m) => {
    if (m.role === 'system') return { role: 'user', content: `[system note] ${m.content}` };
    if (m.role === 'user') return { role: 'user', content: m.content };
    return {
      role: 'assistant',
      content: stripAssistantPlumbing(m.content) || '(prior design omitted for brevity)',
    };
  });

  // Coalesce adjacent same-role messages so the system→user remap never produces
  // two consecutive user messages (Anthropic API rejects that).
  const coalesced: ApiMessage[] = [];
  for (const msg of raw) {
    const last = coalesced[coalesced.length - 1];
    if (
      last &&
      last.role === msg.role &&
      typeof last.content === 'string' &&
      typeof msg.content === 'string'
    ) {
      last.content += `\n\n${msg.content}`;
    } else {
      coalesced.push(msg);
    }
  }

  // Text-file attachments (SVG/HTML/CSS/etc.) inline into the user's prompt under a
  // [Attached: name] header — Claude doesn't have a "text file" content block, but
  // it reads code in fenced blocks fine. Keeps everything in one user message and
  // lets the cache breakpoint (on the design block) still cover the static parts.
  let promptBody = newUserText;
  const textFiles = attachments.filter((a): a is Extract<AttachmentForApi, { kind: 'text-file' }> => a.kind === 'text-file');
  if (textFiles.length > 0) {
    const fileBlocks = textFiles
      .map((f) => {
        const lang = inferLangFromMediaType(f.mediaType, f.name);
        return `\n\n[Attached file: ${f.name} (${f.mediaType})]\n\`\`\`${lang}\n${f.content}\n\`\`\``;
      })
      .join('');
    promptBody = `${promptBody}${fileBlocks}`;
  }

  const richAttachmentCount =
    attachments.filter((a) => a.kind === 'image' || a.kind === 'document').length;
  const designAvailable = Boolean(selectedVersion?.htmlFinal);
  let userContent: string | ApiContent[];

  if (designAvailable || richAttachmentCount > 0) {
    const blocks: ApiContent[] = [];
    for (const a of attachments) {
      if (a.kind === 'image') {
        blocks.push({
          type: 'image' as const,
          source: { type: 'base64' as const, media_type: a.mediaType, data: a.data },
        });
      } else if (a.kind === 'document') {
        blocks.push({
          type: 'document' as const,
          source: { type: 'base64' as const, media_type: 'application/pdf', data: a.data },
        });
      }
      // text-file already folded into promptBody above
    }
    if (designAvailable) {
      const html = selectedVersion!.htmlFinal!;
      const embedded =
        html.length > MAX_ARTIFACT_CONTEXT_CHARS ? structuralOutline(html, opts) : html;
      blocks.push({
        type: 'text',
        text:
          'This is the current state of the design. Modify it in place — preserve all unchanged HTML, CSS, and JS verbatim. Apply only the change(s) requested below.\n\n' +
          `<current_design>\n${embedded}\n</current_design>`,
        cache_control: { type: 'ephemeral' },
      });
    }
    if (promptBody) blocks.push({ type: 'text', text: promptBody });
    userContent = blocks;
  } else {
    userContent = promptBody;
  }

  // Append the new user turn, coalescing with a prior user message if needed.
  const last = coalesced[coalesced.length - 1];
  if (last && last.role === 'user') {
    if (typeof last.content === 'string' && typeof userContent === 'string') {
      last.content += `\n\n${userContent}`;
    } else {
      const lastBlocks: ApiContent[] =
        typeof last.content === 'string'
          ? [{ type: 'text', text: last.content }]
          : last.content;
      const newBlocks: ApiContent[] =
        typeof userContent === 'string'
          ? [{ type: 'text', text: userContent }]
          : userContent;
      last.content = [...lastBlocks, ...newBlocks];
    }
  } else {
    coalesced.push({ role: 'user', content: userContent });
  }

  if (coalesced.length > 0 && coalesced[0]!.role !== 'user') {
    coalesced.unshift({ role: 'user', content: '(begin)' });
  }

  return coalesced;
}
